import yargs from 'yargs';
import path from 'upath';
import fs from 'fs-extra';
import { ListrOptions, ListrTask } from 'listr2';
import chalk from 'chalk';
import { spawn } from 'child_process';
import simpleGit from 'simple-git';
import { isEmpty, map, flatten, split, includes, filter, isObject, isArray, join, isString, assignIn } from 'lodash';

import environment, { EnvVars } from '@bmd-studio/genstack-environment';
import logger from '@bmd-studio/genstack-logger';

import { VersionedServicesConfig, Service, createTaskRunner, ServiceCommandHandler} from './index';
import { ensureBackendNetwork, ensureFrontendNetwork, getDockerEnv } from './docker';

const {
  DOCKER_GENERATED_ENV_FILE
} = environment.env;

const git = simpleGit();

export const buildServices = async () => {
  await executeCommandPerService('build', buildService);
};

export const importServices = async () => {
  await executeCommandPerService('import', importService, {
    concurrent: false
  });
};

export const execServices = async (commandName: string, commandArgv: string[]): Promise<void> => {
  const services = getCurrentServices();
  const serviceTasks = map(services, (service: Service) => {
    const { name } = service;
    const serviceEnv = getServiceEnv(service);
    const serviceRoot = getServiceRoot(service);
    const expandedCommandArgv = expandExecCommandArgv(service, commandName, commandArgv);
    const command = join([commandName, ...expandedCommandArgv], ' ');
    const mergedEnv = expandExecEnv(commandName, serviceEnv);

    const task: ListrTask = {
      title: `Execute ${chalk.cyan(command)} for service ${chalk.underline(name)}`,
      options: {
        persistentOutput: false,
        exitOnError: false,
      },
      task: async (_ctx, task): Promise<void> => {
        return new Promise((resolve, reject) => {
          logger.info.services(`Executing command for service ${chalk.underline(name)} in ${serviceRoot}.`);
          logger.verbose.services(`The environment variables are:`);
          logger.verbose.services(mergedEnv);
          const childProcess = spawn(commandName, expandedCommandArgv, {
            env: mergedEnv,
            cwd: serviceRoot,
            shell: true,
          });
          childProcess.stdout.on('data', (data) => {
            const parsedData = data.toString();
            task.output = parsedData;
          });
          childProcess.stderr.on('data', (data) => {
            const parsedData = data.toString();
            task.output = parsedData;
            logger.warn.services(parsedData);
          });
          childProcess.on('error', (error) => {
            task.skip(`An error occurred: ${error.message}`);
            logger.error.services(error);
            reject(error);
          });
          childProcess.on('exit', (code) => {
            if (code === 0) {
              logger.success.services(`Successfully executed command ${chalk.cyan(command)} for service ${chalk.underline(name)}.`);
              resolve();
              return;
            }
            const error = new Error(`An erroneous exit occured (code: ${code}): ${task.output}`);
            logger.error.services(error);
            reject(error);
          });
        });          
      }
    };

    return task;
  });

  const commandNameTasks = getCommandNameTasks(commandName);
  const taskRunner = createTaskRunner({
    preTasks: commandNameTasks,
    tasks: serviceTasks, 
  });
  await taskRunner.run();
};

const getCommandNameTasks = (commandName: string): ListrTask[] => {
  switch (commandName) {
    case 'docker-compose':
      return [{
        title: 'Initialize default networks',
        task: async(): Promise<void> => {
          await ensureBackendNetwork();
          await ensureFrontendNetwork();
        },
      }];
  }

  return [];
};

const expandExecCommandArgv = (service: Service, commandName: string, commandArgv: string[]): string[] => {
  const envNames = environment.getEnvNames();
  const envType = environment.getEnvType();
  const serviceRoot = getServiceRoot(service);
  let expandedCommandArgv = commandArgv;

  // for docker-compose we have a couple of macro's to make it easier
  // to load up various docker-compose files
  if (commandName === 'docker-compose') {
    const candidateComposeFiles = [
      `docker-compose.yml`,
      `docker-compose.${envType}.yml`,
      ...map(envNames, (envName) => {
        return `docker-compose.${envName}.yml`;
      }),
    ];
    const validComposeFiles = filter(candidateComposeFiles, (composeFile) => {
      const composeFilePath = path.join(serviceRoot, composeFile);
      return fs.existsSync(composeFilePath);
    });
    const envFile = DOCKER_GENERATED_ENV_FILE;
    expandedCommandArgv = [
      `--env-file=${envFile}`,
      ...map(validComposeFiles, (validComposeFile) => {
        return `-f ${validComposeFile}`;
      }),
      ...commandArgv,
    ];
  }

  return expandedCommandArgv;
};

const expandExecEnv = (commandName: string, env: any): any => {
  const expandedEnv = {
    ...process.env,
    ...env,
  };

  switch (commandName) {
    case 'docker-compose':
    case 'docker':
      const dockerEnv = getDockerEnv();
      assignIn(expandedEnv, dockerEnv);
      break;
  }

  return expandedEnv;
};

const importService = async (service: Service) => {
  const { name: serviceName, repository: serviceRepository } = service;
  const { url } = serviceRepository ?? {};
  const serviceRepoRoot = getServiceRepoRoot(service);
  let addError: Error | null = null;
  let pullError: Error | null = null;

  // guard: check if the service repository is defined
  if (!serviceRepository || !url) {
    return;
  }

  try {
    await executeGitSubtreeCommand(service, 'add');
  } catch (error) {
    logger.verbose.services(`Adding service ${chalk.underline(serviceName)} produced error:`);
    logger.verbose.services(error);
    addError = error;
  }

  // always attempt to update to the latest version
  try {
    await executeGitSubtreeCommand(service, 'pull');
  } catch (error) {
    logger.verbose.services(`Pulling service ${chalk.underline(serviceName)} produced error:`);
    logger.verbose.services(error);
    pullError = error;
  }

  // guard: check if the repo now exists
  if (!fs.existsSync(serviceRepoRoot) || pullError) {
    throw Error(`
      The service ${chalk.underline(serviceName)} could not be pulled from ${chalk.underline(url)}! 
      ${addError ? `The add repo error was: ${addError.message}` : ''}
      ${pullError ? `The pull repo error was: ${pullError.message}` : ''}
    `);
  }
};

const executeGitSubtreeCommand = async (service: Service, command: string, refOverride = '') => {
  const { name: serviceName, repository: serviceRepository } = service;
  const { url = '', ref = 'master' } = serviceRepository ?? {};
  const gitRootPath = await getGitRoot();
  const serviceRepoRoot = getServiceRepoRoot(service);
  const checkoutPath = path.relative(gitRootPath, serviceRepoRoot);
  const usedRef = refOverride ? refOverride : ref;

  // guard: check if the service repository is defined
  if (!serviceRepository) {
    return;
  }

  // always make sure the GIT commands are being run from the GIT root
  git.cwd(gitRootPath);
  
  logger.verbose.services(`Executing subtree ${chalk.underline(command)} for service ${chalk.underline(serviceName)} (${chalk.underline(url)}) into ${chalk.underline(checkoutPath)}...`);
  return git.raw(['subtree', command, '--prefix', checkoutPath, url, usedRef, '--squash']);
};

/**
 * Get the root of the GIT repository seen from the current working directory.
 */
const getGitRoot = async (): Promise<string> => {
  return await git.revparse(['--show-toplevel']);
};

/**
 * Build a single specific service.
 * @param service Service entry to build.
 */
const buildService = async (service: Service) => {
  const { name: serviceName, repository: serviceRepo } = service;
  const serviceRepoRoot = getServiceRepoRoot(service);
  // const servicePathRoot = getServiceRoot(service);
  const validRepoRoot = !isEmpty(serviceRepo) && fs.existsSync(serviceRepoRoot);
  // const validPathRoot = !isEmpty(servicePath) && fs.existsSync(servicePathRoot);
  const serviceBuildDir = getServiceBuildPath(service);
  const env = getServiceEnv(service);
  const envString = join(map(env, (value, key) => {
    return `${key}=${value}`;
  }), `\n`);

  logger.info.services(`Building service ${chalk.underline(serviceName)} in ${chalk.underline(serviceBuildDir)}...`);

  // guard: check if the repo is checked out
  if (!validRepoRoot && !isEmpty(serviceRepo)) {
    throw Error(`The repository of ${chalk.underline(serviceName)} is not checked out yet. Have you initialized all the repositories using the 'pull' command?`);
  }

  // write the env file to the generated docker files
  logger.verbose.services(`Writing the generated environment variables into build...`);
  fs.writeFileSync(path.resolve(serviceBuildDir, DOCKER_GENERATED_ENV_FILE), envString);

  logger.info.services(`The service ${chalk.underline(serviceName)} was successfully built.`);
};

const executeCommandPerService = async (command: string, handler: ServiceCommandHandler, taskOptions: ListrOptions = {}) => {
  const services = getCurrentServices();

  const tasks = map(services, (service: Service) => {
    const { name } = service;
    const task: ListrTask = {
      title: `Execute ${chalk.underline(command)} for service ${chalk.underline(name)}`,
      options: {
        persistentOutput: false,
        exitOnError: false,
      },
      task: async () => {
        await handler(service);         
      }
    };

    return task;
  });

  const taskRunner = createTaskRunner({
    tasks,
    taskOptions,
  });
  await taskRunner.run();
};

/**
 * Construct an object of environment variables for a specific service by loading all shared
 * environment variables and checking the ones that are specific for the requested service.
 * @param service The service entry to construct the environment variable set for
 */
export const getServiceEnv = (service: Service): EnvVars => {
  const { name: serviceName, env: serviceEnv } = service;
  const envName = environment.getEnvName();
  const serviceRepoRoot = getServiceRepoRoot(service);
  const servicePathRoot = getServiceRoot(service);
  const serviceBuildPath = getServiceBuildPath(service);
  const serviceRepoDefaultsEnv = environment.loadEnvFile(path.resolve(serviceRepoRoot, 'env/defaults'));
  const serviceRepoEnv = environment.loadEnvFile(path.resolve(serviceRepoRoot, 'env', envName));
  const servicePathDefaultsEnv = environment.loadEnvFile(path.resolve(servicePathRoot, 'env/defaults'));
  const servicePathEnv = environment.loadEnvFile(path.resolve(servicePathRoot, 'env', envName));
  const configEnv = environment.getConfigEnvVarsUnparsed();

  // stringify the services.js env entries to 
  // avoid the automatic "[object Object]" toString
  if (serviceEnv) {
    Object.entries(serviceEnv).map(([key, value]) =>
      isObject(value) && !isArray(value)
        ? serviceEnv[key] = JSON.stringify(value)
        : null
    );
  }

  // merge all the environment variables for this service
  const mergedServiceEnv = {
    ...serviceRepoDefaultsEnv,
    ...servicePathDefaultsEnv,
    ...serviceRepoEnv,
    ...configEnv,
    ...servicePathEnv,
    ...serviceEnv,
    SERVICE_NAME: serviceName,
    SERVICE_BUILD_PATH: serviceBuildPath,
  };

  return mergedServiceEnv;
};

/**
 * Get all the active services based on a set of requested services via the `arguments`,
 * requested services via `CLI command flags` and the `service config`. 
 * This allows for customization of what service to build, start or stop.
 * @param requestedServices Array of service names
 * @param requestedOnly True when any requested services from CLI arguments or `requestedServices` parameter should filter the result
 */
export const getCurrentServices = (): Service[] => {
  const services = getProjectServices();
  const requestedServices = getRequestedServices();

  // only get the active and requested services
  const activeServices = filter(services, (service: Service) => {
    const { active, name } = service;

    // guard: skip services that are not active
    if (!active) {
      return false;
    }

    // guard: check if a specific set is requested
    if (!isEmpty(requestedServices) && !includes(requestedServices, name)) {
      return false;
    }

    return true;
  });

  return activeServices;
};

/**
 * Get the subset of service names that is requested for this command.
 */
export const getRequestedServices = (): string[] => {
  const argv = yargs.argv;
  const rawServiceArg = argv?.service;
  let requestedServices = rawServiceArg as string[] || [];

  if (isString(rawServiceArg)) {
    requestedServices = [rawServiceArg];
  }

  requestedServices = flatten(map(requestedServices, (serviceName) => {
    return split(serviceName, `,`);
  }));

  return requestedServices;
};

/**
 * Get all the service definitions from the application config.
 */
export const getProjectServices = (): Service[] => {
  const genstackConfig = environment.getGenstackConfig();
  const servicesConfig = environment.loadConfig<VersionedServicesConfig>({
    resource: genstackConfig?.generators?.docker?.servicesConfig,
  });
  const services: Service[] = servicesConfig?.config?.services ?? [];

  // guard: check if a config could be found
  if (isEmpty(servicesConfig)) {
    throw Error(`No valid services definition was found in the 'generators.docker.services' config.`);
  }

  return services;
};

/**
 * Get the root of a service that is defined for this project.
 * @param service The service.
 * @param args Optional deconstructed subdirectories.
 */
export const getServiceRoot = (service: Service, ...args: string[]): string => {
  const { path: servicePath, repository } = service;
  const projectRoot = environment.getProjectRoot();

  if (repository) {
    return getServiceRepoRoot(service, ...args);
  }

  if (!servicePath) {
    return '';
  }

  return path.join(projectRoot, servicePath, ...args);
}; 

/**
 * Get the root of a service that is build from an existing repository.
 * @param service The service.
 * @param args Optional deconstructed subdirectories.
 */
export const getServiceRepoRoot = (service: Service, ...args: string[]): string => {
  const { name: serviceName } = service;
  const projectRoot = environment.getProjectRoot();
  const genstackConfig = environment.getGenstackConfig();
  const servicesRepoPath = genstackConfig?.generators?.docker?.output?.servicesRepoPath;

  return path.join(projectRoot, servicesRepoPath, serviceName, ...args);
};

/**
 * Get the root to the application service build directory
 * @param args Optional deconstructed subdirectories.
 */  
export const getServiceBuildPath = (service: Service): string => {
  const { name: serviceName, path: servicePath, repository } = service;
  const serviceRepoRoot = getServiceRepoRoot(service);
  const servicePathRoot = environment.getProjectRoot(servicePath ?? '');
  const validRepoRoot = !isEmpty(repository) && fs.existsSync(serviceRepoRoot);
  const validPathRoot = !isEmpty(servicePath) && fs.existsSync(servicePathRoot);
  let buildPath = '';

  if (validRepoRoot) {
    buildPath = serviceRepoRoot;
  } else if (validPathRoot) {
    buildPath = servicePathRoot
  }

  if (isEmpty(buildPath)) {
    throw Error(`Could not get a valid service build path for service ${chalk.underline(serviceName)}.`);
  }

  return buildPath;
};